<template>
  <!-- dir="ltr": a time→goal line chart. The plot lives in a fixed SVG viewBox
       (physical x increases left→right, unaffected by CSS dir) and the start/end
       pills + captions are absolutely positioned from percentages derived from
       those viewBox coordinates. Keeping the whole chart LTR keeps the overlays
       aligned with the physical plot in every locale. -->
  <div class="goal-line-chart" :class="{ 'is-animating': playhead !== 'idle' }" dir="ltr">
    <svg
      class="goal-line-chart__svg"
      :viewBox="`0 0 ${VB_W} ${effectiveVbH}`"
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label="Goal progress chart"
    >
      <defs v-if="css.lineShadow">
        <filter
          :id="shadowId"
          x="-20"
          y="-20"
          :width="VB_W + 40"
          :height="VB_H + 40"
          filterUnits="userSpaceOnUse"
          color-interpolation-filters="sRGB"
        >
          <feGaussianBlur in="SourceGraphic" stdDeviation="6" result="blur" />
          <feOffset in="blur" dy="4" result="shifted" />
          <feFlood :flood-color="css.lineShadowColor" flood-opacity="0.8" result="tint" />
          <feComposite in="tint" in2="shifted" operator="in" result="shadow" />
          <feMerge>
            <feMergeNode in="shadow" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      <g class="gridlines">
        <line
          v-for="(x, i) in gridlineXs"
          :key="i"
          :x1="x"
          :x2="x"
          :y1="GRID_Y0"
          :y2="GRID_Y1"
          :stroke="
            i === 0 || i === gridlineXs.length - 1 ? css.gridlineAccentColor : css.gridlineColor
          "
          :stroke-width="css.gridlineWidth"
        />
      </g>

      <path
        v-if="isAnimationActive"
        class="ghost-line"
        :d="linePath"
        :stroke="css.lineColor"
        :stroke-width="css.lineWidth"
        stroke-linecap="round"
        fill="none"
        :stroke-opacity="previewOpacity"
      />

      <path
        class="progress-line"
        :d="linePath"
        :stroke="css.lineColor"
        :stroke-width="css.lineWidth"
        stroke-linecap="round"
        fill="none"
        :filter="css.lineShadow ? `url(#${shadowId})` : undefined"
        :style="lineStyle"
        ref="lineRef"
      />

      <circle
        v-if="
          (animType === 'travel-dot' || animType === 'draw-line-with-dot') && playhead !== 'idle'
        "
        class="travel-dot"
        :cx="dotX"
        :cy="dotY"
        :r="markerOuterR * 0.6"
        :fill="css.markerColor"
      />

      <g v-if="fields.showMarkers !== false" class="markers">
        <circle :cx="START_X" :cy="START_Y" :r="markerOuterR" :fill="css.markerColor" />
        <circle :cx="START_X" :cy="START_Y" :r="markerInnerR" :fill="css.markerInnerColor" />
        <circle
          :cx="END_X"
          :cy="END_Y"
          :r="markerOuterR"
          :fill="css.markerColor"
          :opacity="endMarkerOpacity"
        />
        <circle
          :cx="END_X"
          :cy="END_Y"
          :r="markerInnerR"
          :fill="css.markerInnerColor"
          :opacity="endMarkerOpacity"
        />
      </g>

      <foreignObject
        v-if="fields.showCaptions !== false"
        :x="START_CAPTION.x"
        :y="START_CAPTION.y"
        :width="START_CAPTION.w"
        :height="START_CAPTION.h"
        overflow="visible"
      >
        <div class="caption caption-start">
          <div class="caption-line">
            <TipTap v-if="fields.startCaption?.key" v-model="startCaptionValue" mode="single" />
          </div>
          <div class="caption-line caption-sub">
            <TipTap
              v-if="fields.startCaptionSub?.key"
              v-model="startCaptionSubValue"
              mode="single"
            />
          </div>
        </div>
      </foreignObject>

      <foreignObject
        v-if="fields.showCaptions !== false"
        :x="END_CAPTION.x"
        :y="END_CAPTION.y"
        :width="END_CAPTION.w"
        :height="END_CAPTION.h"
        overflow="visible"
      >
        <div class="caption caption-end">
          <div class="caption-line">
            <TipTap v-if="fields.endCaption?.key" v-model="endCaptionValue" mode="single" />
          </div>
          <div class="caption-line caption-sub">
            <TipTap v-if="fields.endCaptionSub?.key" v-model="endCaptionSubValue" mode="single" />
          </div>
        </div>
      </foreignObject>
    </svg>

    <!-- The rich text is wrapped in ONE element on purpose — see `.pill-text`. -->
    <div v-if="fields.showStartPill !== false" class="pill pill-start">
      <span
        ><TipTap v-if="fields.startPillLabel?.key" v-model="startPillValue" mode="single"
      /></span>
    </div>
    <div v-if="fields.showEndPill !== false" class="pill pill-end">
      <span><TipTap v-if="fields.endPillLabel?.key" v-model="endPillValue" mode="single" /></span>
    </div>
    <div ref="captionDistanceProbe" class="size-probe" aria-hidden="true" />
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useId, watch } from 'vue'
import { PipeDisplayMode, isInPipeContext, usePipe } from '@render/composables/usePipe'
import { useComponentProcessors } from '@render/composables/useComponentProcessors'
import { useEditorCommands } from '@render/composables/useEditorCommands'
import { useComponentScopedVariables } from '@render/composables/useComponentScopedVariables'
import {
  getColorStyleData,
  getSizeStyleData,
  getSpacingStyleData,
} from '@render/composables/useStyleData'
import TipTap from '@render/lib/TipTap'
import type { ColorField, SizeField } from '@render/types/styles'
import type { VariableKeyField } from '@api/types.gen'
import { DEFAULT_GOAL_PACE_KG, getDaysTillGoalDate, getGoalDate } from '@render/lib/weightCalc'
import { formatLocalIsoDate } from '@render/lib/dateCalc'
import type {
  GoalLineChartFields as Fields,
  LineShape,
  AnimationTrigger,
  AnimationType,
} from './definition'

const fields = defineModel<Fields>('fields', {
  required: true,
  default: (): Fields => ({}),
})

const pipe = isInPipeContext() ? usePipe() : null

// SVG viewBox is fixed; width is 100% and height scales via aspect-ratio or
// the explicit chartHeight override. Coordinates below are chosen to mirror
// goalchart.svg so the chart reads the same after the paths move in-template.
const VB_W = 480
const VB_H = 268

const GRID_Y0 = 28
const GRID_Y1 = 236
const PLOT_X0 = 45
const PLOT_X1 = 432

const START_X = 45
const START_Y = 52
const END_X = 434
const END_Y = 212

// Anchor each pill by its bottom edge just above its marker, so long labels grow
// upward instead of covering the dot. The effectiveVbH denominator keeps the
// anchor locked to the marker as captionDistance extends the viewBox.
const PILL_DOT_GAP = 4 // viewBox units above the marker
const pctFromBottom = (anchorY: number) =>
  `${((effectiveVbH.value - anchorY) / effectiveVbH.value) * 100}%`
const startPillBottom = computed(() => pctFromBottom(START_Y - markerOuterR.value - PILL_DOT_GAP))
const endPillBottom = computed(() => pctFromBottom(END_Y - markerOuterR.value - PILL_DOT_GAP))
const pctOfWidth = (vbUnits: number) => `${(vbUnits / VB_W) * 100}%`
const startPillRoom = computed(() =>
  pctOfWidth(Math.max(0, START_Y - markerOuterR.value - PILL_DOT_GAP)),
)
const endPillLift = computed(() =>
  pctOfWidth(effectiveVbH.value - (END_Y - markerOuterR.value - PILL_DOT_GAP)),
)
// Captions split the bottom in half — start hugs the left edge, end hugs the
// right edge, and they meet in the middle. Height is tall enough to stack the
// caption + sub on two lines without clipping the sub at the chart bottom.
// `captionDistance` is a `cssSizeVar` (SizeField) and is added to the default
// caption Y. SVG attributes (`y`, `viewBox`) need a unitless number, so we
// resolve the size to pixels via a hidden DOM probe (see `size-probe` below)
// — this lets stylesheet refs (`var(--…)`) and any unit (px/em/rem/%) work
// uniformly, since the browser handles the cascade resolution for us.
const CAPTION_DEFAULT_Y = 228
const captionDistanceProbe = ref<HTMLDivElement | null>(null)
const resolvedCaptionDistancePx = ref(0)
const captionDistanceCss = computed(() => getSizeStyleData(fields.value.captionDistance) || '0px')
function measureCaptionDistance() {
  const el = captionDistanceProbe.value
  if (!el) return
  resolvedCaptionDistancePx.value = el.getBoundingClientRect().height
}

// Defer the size write out of the ResizeObserver delivery cycle — writing
// reactive state that feeds the SVG viewBox synchronously inside the callback
// can trigger a same-frame resize, which the browser reports as
// "ResizeObserver loop completed with undelivered notifications".
let captionDistanceRaf = 0
function scheduleMeasureCaptionDistance() {
  if (captionDistanceRaf) return
  captionDistanceRaf = requestAnimationFrame(() => {
    captionDistanceRaf = 0
    measureCaptionDistance()
  })
}
let captionDistanceRO: ResizeObserver | null = null
const captionTopY = computed(() => CAPTION_DEFAULT_Y + resolvedCaptionDistancePx.value)
// Effective viewBox height — extends downward when captionDistance pushes
// the caption block past the original VB_H so the chart-wrap's aspect ratio
// also grows. Without this, the chart-wrap height stays locked to the
// original ratio and the captions overflow into whatever sits below.
const effectiveVbH = computed(() => VB_H + Math.max(0, resolvedCaptionDistancePx.value))
const START_CAPTION = computed(() => ({
  x: 0,
  y: captionTopY.value,
  w: 240,
  h: 40,
}))
const END_CAPTION = computed(() => ({
  x: 240,
  y: captionTopY.value,
  w: 240,
  h: 40,
}))

const shadowId = `goal-line-shadow-${useId()}`

// ---------- translatable labels ----------

function translationModel(keyPath: () => string | undefined) {
  return computed<object>({
    get: () => {
      const key = keyPath()
      if (!key || !pipe) return {}
      return pipe.getTranslation(key) ?? {}
    },
    set: (val) => {
      const key = keyPath()
      if (!key || !pipe) return
      pipe.setTranslation(key, val)
    },
  })
}

const startPillValue = translationModel(() => fields.value.startPillLabel?.key)
const endPillValue = translationModel(() => fields.value.endPillLabel?.key)
const startCaptionValue = translationModel(() => fields.value.startCaption?.key)
const startCaptionSubValue = translationModel(() => fields.value.startCaptionSub?.key)
const endCaptionValue = translationModel(() => fields.value.endCaption?.key)
const endCaptionSubValue = translationModel(() => fields.value.endCaptionSub?.key)

// ---------- derived css ----------

function col(f: ColorField | undefined, fallback: string): string {
  return getColorStyleData(f) || fallback
}
function sz(f: SizeField | undefined, fallback: string): string {
  return getSizeStyleData(f) || fallback
}

const css = computed(() => ({
  margin: getSpacingStyleData(fields.value.margin, '0'),
  chartHeight: getSizeStyleData(fields.value.chartHeight) || 'none',
  // Aspect-ratio uses the effective viewBox height so increasing the
  // caption distance grows the chart-wrap downward (taking room from the
  // surrounding flow) instead of overflowing onto whatever sits below.
  aspectRatio: `${VB_W} / ${effectiveVbH.value}`,
  // When chartHeight is set, clamp width to chartHeight × (VB_W/VB_H) so the
  // aspect-ratio stays locked — without this clamp the browser clips height
  // but keeps width at 100%, skewing pill percent-positions off the content.
  chartMaxWidth: fields.value.chartHeight
    ? `calc(${getSizeStyleData(fields.value.chartHeight)} * ${VB_W} / ${effectiveVbH.value})`
    : 'none',

  lineColor: col(fields.value.lineColor, 'var(--color-action-primary-background)'),
  lineWidth: sz(fields.value.lineWidth, '6'),
  lineShadow: fields.value.lineShadow !== false,
  lineShadowColor: col(fields.value.lineShadowColor, 'var(--color-action-tertiary-background)'),

  gridlineAccentColor: col(
    fields.value.gridlineAccentColor,
    'var(--color-action-primary-background)',
  ),
  gridlineColor: col(fields.value.gridlineColor, 'var(--color-action-tertiary-border)'),
  gridlineWidth: sz(fields.value.gridlineWidth, '1'),

  markerColor: col(fields.value.markerColor, 'var(--color-action-primary-background)'),
  markerInnerColor: col(fields.value.markerInnerColor, '#ffffff'),
  markerSize: sz(fields.value.markerSize, '32'),

  pillBg: col(fields.value.pillBgColor, 'var(--color-action-tertiary-background)'),
  pillBorder: col(fields.value.pillBorderColor, 'var(--color-action-primary-background)'),
  pillText: col(fields.value.pillTextColor, 'var(--text-body-md-color)'),
  pillFontSize: sz(fields.value.pillFontSize, 'var(--text-body-md-size)'),
  pillFontWeight: fields.value.pillFontWeight || 'var(--text-body-md-weight)',
  // Optional author overrides — default to the CSS no-op so an untouched pill
  // sizes to its content exactly as before.
  pillMinWidth: getSizeStyleData(fields.value.pillMinWidth) || '48px',
  pillMaxWidth: getSizeStyleData(fields.value.pillMaxWidth) || 'none',
  pillMinHeight: getSizeStyleData(fields.value.pillMinHeight) || 'auto',
  pillMaxHeight: getSizeStyleData(fields.value.pillMaxHeight) || 'none',

  captionColor: col(fields.value.captionColor, 'var(--text-body-md-color)'),
  captionSubColor: col(fields.value.captionSubColor, 'var(--color-text-secondary)'),
  captionFontSize: sz(fields.value.captionFontSize, 'var(--text-body-md-size)'),
  captionSubFontSize: sz(fields.value.captionSubFontSize, 'var(--text-body-sm-size)'),
  captionFontWeight: fields.value.captionFontWeight || 'var(--text-body-md-weight)',
}))

// Outer marker radius is derived from markerSize, which may be a unit string
// (e.g. "32" or "var(--space-32)"). getTotalLength etc. need a number, but
// for the circle `r` attribute SVG accepts unitless values in user-space.
const markerOuterR = computed(() => {
  const raw = fields.value.markerSize
  if (raw?.type === 'size_raw') return Math.max(4, Number(raw.data.data) / 2)
  return 16
})
const markerInnerR = computed(() => Math.max(2, markerOuterR.value * 0.375))

const gridlineCount = computed(() => {
  const n = Number(fields.value.gridlineCount ?? 4)
  return Number.isFinite(n) ? Math.max(2, Math.floor(n)) : 4
})

const gridlineXs = computed<number[]>(() => {
  const n = gridlineCount.value
  const step = (PLOT_X1 - PLOT_X0) / (n - 1)
  return Array.from({ length: n }, (_, i) => PLOT_X0 + step * i)
})

// ---------- line path ----------

const lineShape = computed<LineShape>(() => fields.value.lineShape ?? 'smooth')

const linePath = computed(() => {
  const shape = lineShape.value
  const x0 = START_X
  const y0 = START_Y
  const x1 = END_X
  const y1 = END_Y
  const mx = (x0 + x1) / 2

  switch (shape) {
    case 'straight':
      return `M ${x0} ${y0} L ${x1} ${y1}`
    case 'step':
      return `M ${x0} ${y0} H ${mx} V ${y1} H ${x1}`
    case 's-curve':
      return `M ${x0} ${y0} C ${mx} ${y0}, ${mx} ${y1}, ${x1} ${y1}`
    case 'ease-out':
      // fast drop, then plateau — control points pull curve toward end y early
      return `M ${x0} ${y0} C ${x0 + 75} ${y1 - 40}, ${mx + 50} ${y1}, ${x1} ${y1}`
    case 'ease-in':
      // slow start, fast drop at end
      return `M ${x0} ${y0} C ${mx - 50} ${y0}, ${x1 - 75} ${y0 + 40}, ${x1} ${y1}`
    case 'concave':
      // dips below the end Y then comes back up
      return `M ${x0} ${y0} C ${x0 + 100} ${y1 + 40}, ${x1 - 100} ${y1 + 40}, ${x1} ${y1}`
    case 'smooth':
    default:
      // Lifted from goalchart.svg with endpoints aligned to START/END.
      return `M ${x0} ${y0} C 60.5 102, 127 179, ${mx.toFixed(1)} 186.14 S 348.61 193, ${x1} ${y1}`
  }
})

// ---------- animation ----------

const animTrigger = computed<AnimationTrigger>(() => fields.value.animationTrigger ?? 'none')
const animType = computed<AnimationType>(() => fields.value.animationType ?? 'draw-line')
const animDurationMs = computed(() => Math.max(0, Number(fields.value.animationDuration ?? 1500)))
const animDelayMs = computed(() => Math.max(0, Number(fields.value.animationDelay ?? 0)))
const previewOpacity = computed(() => {
  const raw = Number(fields.value.previewTransparency)
  if (!Number.isFinite(raw)) return 0.3
  return Math.max(0, Math.min(1, raw))
})

const isEditor = computed(() => pipe?.currentMode.value === PipeDisplayMode.EDITOR)

// In editor mode, don't auto-animate — authors don't want the line flashing
// every time they tweak an unrelated field. "Preview Animation" and the
// component-processor both call startAnimation manually. Matches IconList.
const previewing = ref(false)
const animLive = computed(() => !isEditor.value || previewing.value)
const effectiveTrigger = computed<AnimationTrigger>(() =>
  animLive.value ? animTrigger.value : 'none',
)

// Playhead: 'idle' = natural steady state (no style overrides), 'from' = the
// pre-animation frame (e.g. stroke-dashoffset = path length, opacity = 0), 'to'
// = the post-animation frame with transition attached. The explicit 'from'
// frame is required so the browser has a starting value to transition from —
// without it CSS flips straight to the end state with no visible animation.
const playhead = ref<'idle' | 'from' | 'to'>('idle')
const runId = ref(0)
const pathLength = ref(1000)
const lineRef = ref<SVGPathElement | null>(null)

let animTimer: ReturnType<typeof setTimeout> | null = null

// Travel-dot uses a JS rAF loop rather than SMIL <animateMotion> — SMIL
// elements inserted into a Vue-managed subtree don't always auto-start
// reliably across browsers, so we drive cx/cy off getPointAtLength instead.
const dotX = ref(START_X)
const dotY = ref(START_Y)
// Shared 0..1 progress for the current animation run. Advanced by the single
// rAF loop below for every animationType, not just travel-dot, so that
// derived values (e.g. component-scoped variables exposed via
// useComponentScopedVariables) tween in lockstep with the visual animation.
const animatedT = ref(1)
let tickRaf: number | null = null

function clearAnimTimer() {
  if (animTimer) {
    clearTimeout(animTimer)
    animTimer = null
  }
}

function stopTickLoop() {
  if (tickRaf !== null) cancelAnimationFrame(tickRaf)
  tickRaf = null
}

function runTickLoop() {
  const line = lineRef.value
  let len = 0
  if (line) {
    try {
      len = line.getTotalLength()
    } catch {
      len = 0
    }
  }

  const startTs = performance.now() + animDelayMs.value
  // Seed dot to the first point so the dot shows up at the start before motion.
  if (
    (animType.value === 'travel-dot' || animType.value === 'draw-line-with-dot') &&
    line &&
    Number.isFinite(len) &&
    len > 0
  ) {
    try {
      const p0 = line.getPointAtLength(0)
      dotX.value = p0.x
      dotY.value = p0.y
    } catch {
      // ignore — dotX/dotY retain their current seed
    }
  }

  const tick = (now: number) => {
    const elapsed = now - startTs
    if (elapsed < 0) {
      tickRaf = requestAnimationFrame(tick)
      return
    }
    const t = animDurationMs.value > 0 ? Math.min(1, elapsed / animDurationMs.value) : 1
    animatedT.value = t
    if (
      (animType.value === 'travel-dot' || animType.value === 'draw-line-with-dot') &&
      line &&
      Number.isFinite(len) &&
      len > 0
    ) {
      try {
        const pt = line.getPointAtLength(t * len)
        dotX.value = pt.x
        dotY.value = pt.y
      } catch {
        // ignore and continue — dot just stops advancing
      }
    }
    if (t < 1) {
      tickRaf = requestAnimationFrame(tick)
    } else {
      tickRaf = null
    }
  }
  tickRaf = requestAnimationFrame(tick)
}

function measurePath() {
  const el = lineRef.value
  if (!el) return
  try {
    const len = el.getTotalLength()
    if (Number.isFinite(len) && len > 0) pathLength.value = len
  } catch {
    // getTotalLength can throw in unusual environments (jsdom) — leave the
    // previous length in place.
  }
}

function startAnimation() {
  clearAnimTimer()
  stopTickLoop()
  previewing.value = true
  runId.value++
  playhead.value = 'from'
  animatedT.value = 0
  // Paint the 'from' frame, then flip to 'to' on the NEXT frame so the CSS
  // transition has a valid starting value to animate from. Two rAFs because a
  // single rAF lands in the same frame as the pending style commit in some
  // browsers (esp. when startAnimation fires mid-layout).
  nextTick(() => {
    measurePath()
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        playhead.value = 'to'
        runTickLoop()
      })
    })
  })
  const total = animDurationMs.value + animDelayMs.value + 200
  animTimer = setTimeout(() => {
    previewing.value = false
    playhead.value = 'idle'
    stopTickLoop()
    animatedT.value = 1
    animTimer = null
    // Mark the run as completed so `liveWaiting` doesn't re-paint the
    // pre-animation 'from' state once the animation finishes (was causing
    // the drawn line / end marker to flip back to transparent in 'mount'
    // / 'event' live modes).
    hasAnimated.value = true
  }, total)
}

type CssStyle = Record<string, string>

// A live (non-editor) run with trigger='mount' or 'event' also wants the
// pre-animation style painted before startAnimation flips to 'to'. For 'event'
// specifically the line should stay in the 'from' state until the event fires.
// Once an animation has completed at least once, suppress this — otherwise
// the post-completion `playhead='idle'` re-satisfies the condition and the
// line / end-marker flip back to the transparent / hidden 'from' state.
const hasAnimated = ref(false)
const liveWaiting = computed(
  () =>
    !hasAnimated.value &&
    playhead.value === 'idle' &&
    animLive.value &&
    effectiveTrigger.value !== 'none',
)

// True for the 'draw-line-with-dot' type while the run is either pending
// ('event' trigger waiting for a fire), paused on the 'from' frame, or
// actively transitioning ('to'). The ghost line + dimmed end marker key off
// this — once the animation finishes (playhead returns to 'idle' and
// liveWaiting is false), they revert to their normal opacity.
const isAnimationActive = computed(
  () => animType.value === 'draw-line-with-dot' && (playhead.value !== 'idle' || liveWaiting.value),
)
const endMarkerOpacity = computed(() => (isAnimationActive.value ? previewOpacity.value : 1))

const lineStyle = computed<CssStyle>(() => {
  const type = animType.value
  const len = pathLength.value
  const dur = `${animDurationMs.value}ms`
  const delay = `${animDelayMs.value}ms`
  const showFrom = playhead.value === 'from' || liveWaiting.value
  const showTo = playhead.value === 'to'

  if (type === 'draw-line') {
    if (showTo) {
      const s: CssStyle = {}
      s.strokeDasharray = `${len}`
      s.strokeDashoffset = '0'
      s.transition = `stroke-dashoffset ${dur} ease ${delay}`
      return s
    }
    if (showFrom) {
      const s: CssStyle = {}
      s.strokeDasharray = `${len}`
      s.strokeDashoffset = `${len}`
      return s
    }
    return {}
  }
  // 'draw-line-with-dot' is JS-driven (no CSS transition) so the line tip
  // and the dot stay aligned: both use the same linear `animatedT`. Driving
  // the line via CSS transition would put it on `ease` while the dot — fed
  // by getPointAtLength(t * len) — runs linearly, leaving the dot ahead of
  // the drawn tip in the middle and behind it near the ends.
  if (type === 'draw-line-with-dot') {
    if (showTo) {
      const s: CssStyle = {}
      s.strokeDasharray = `${len}`
      s.strokeDashoffset = `${len * (1 - animatedT.value)}`
      return s
    }
    if (showFrom) {
      const s: CssStyle = {}
      s.strokeDasharray = `${len}`
      s.strokeDashoffset = `${len}`
      return s
    }
    return {}
  }
  if (type === 'fade-in') {
    if (showTo) {
      const s: CssStyle = {}
      s.opacity = '1'
      s.transition = `opacity ${dur} ease ${delay}`
      return s
    }
    if (showFrom) {
      const s: CssStyle = {}
      s.opacity = '0'
      return s
    }
    return {}
  }
  if (type === 'grow-from-start') {
    if (showTo) {
      const s: CssStyle = {}
      s.transform = 'scale(1, 1)'
      s.transformOrigin = `${START_X}px ${START_Y}px`
      s.transition = `transform ${dur} ease ${delay}`
      return s
    }
    if (showFrom) {
      const s: CssStyle = {}
      s.transform = 'scale(0.001, 1)'
      s.transformOrigin = `${START_X}px ${START_Y}px`
      return s
    }
    return {}
  }
  return {}
})

function applyTriggerMode(mode: AnimationTrigger) {
  clearAnimTimer()
  if (mode === 'mount') {
    startAnimation()
  } else if (mode === 'none') {
    playhead.value = 'idle'
    previewing.value = false
  } else {
    // 'event' — render the 'from' state and stay there until someone fires
    // the processor (liveWaiting computed handles this via playhead='idle').
    playhead.value = 'idle'
  }
}

watch(effectiveTrigger, applyTriggerMode)

watch(lineShape, () => {
  nextTick(measurePath)
})

onMounted(() => {
  applyTriggerMode(effectiveTrigger.value)
  nextTick(measurePath)
  measureCaptionDistance()
  if (captionDistanceProbe.value && typeof ResizeObserver !== 'undefined') {
    captionDistanceRO = new ResizeObserver(scheduleMeasureCaptionDistance)
    captionDistanceRO.observe(captionDistanceProbe.value)
  }
})

onBeforeUnmount(() => {
  clearAnimTimer()
  stopTickLoop()
  captionDistanceRO?.disconnect()
  captionDistanceRO = null
  if (captionDistanceRaf) cancelAnimationFrame(captionDistanceRaf)
})

useComponentProcessors({
  startAnimation: () => startAnimation(),
})

useEditorCommands({
  startAnimation: () => startAnimation(),
})

// Resolve weight/target/pace from bound variables with a fallback chain:
//   variable value → fallbackGoalPaceKg (pace only) → DEFAULT_GOAL_PACE_KG / 0.
// We go through pipe.getData so the key-shape of `pipe.variables` stays
// abstracted (flat vs nested) — the composable handles it.
function readVarNumber(v: VariableKeyField | undefined): number {
  if (!v?.key || !pipe) return 0
  const raw = pipe.getData(`var.${v.key}`)
  const n = Number(raw)
  return Number.isFinite(n) ? n : 0
}

const weightKg = computed(() => readVarNumber(fields.value.weightVariable))
const targetWeightKg = computed(() => readVarNumber(fields.value.targetWeightVariable))
const goalPaceKg = computed(() => {
  const fromVar = readVarNumber(fields.value.goalPaceVariable)
  if (fromVar > 0) return fromVar
  const fallback = Number(fields.value.fallbackGoalPaceKg)
  return Number.isFinite(fallback) && fallback > 0 ? fallback : DEFAULT_GOAL_PACE_KG
})

// Component-scoped variables — available in TipTap interpolation (e.g.
// `{{comp.animatedWeight}}`, `{{comp.goalDate}}`) for any TipTap descendants.
// Getter form so Vue tracks the refs touched and the NodeView re-reads on
// each animatedT tick.
const animatedWeight = computed(() => {
  const start = weightKg.value
  const target = targetWeightKg.value
  return start + (target - start) * animatedT.value
})

// `goalPaceKg` above already resolves the variable-or-field fallback, so
// these derive correctly whether the user bound a `goalPaceVariable` or
// just picked a value via the `fallbackGoalPaceKg` field.
const daysTillGoalDate = computed(() =>
  getDaysTillGoalDate({
    weightKg: weightKg.value,
    targetWeightKg: targetWeightKg.value,
    goalPaceKg: goalPaceKg.value,
  }),
)

const goalDate = computed(
  () =>
    formatLocalIsoDate(
      getGoalDate({
        weightKg: weightKg.value,
        targetWeightKg: targetWeightKg.value,
        goalPaceKg: goalPaceKg.value,
      }),
    ) ?? '',
)

useComponentScopedVariables('GoalLineChart', () => ({
  weightKg: weightKg.value,
  targetWeightKg: targetWeightKg.value,
  goalPaceKg: goalPaceKg.value,
  animatedWeight: animatedWeight.value,
  progress: animatedT.value,
  daysTillGoalDate: daysTillGoalDate.value,
  goalDate: goalDate.value,
}))
</script>

<style scoped lang="scss">
.goal-line-chart {
  display: block;
  position: relative;
  // Lock the wrapper aspect to the SVG viewBox so the absolutely-positioned
  // pills — which use percentages derived from viewBox coordinates — sit on
  // the same visual content as the SVG-internal markers/line. Block-level
  // auto-width (no `width: 100%`) lets the box shrink to fit available space
  // when margin is set, instead of overflowing parent + margin to the right.
  aspect-ratio: v-bind('css.aspectRatio');
  max-width: v-bind('css.chartMaxWidth');
  max-height: v-bind('css.chartHeight');
  margin: v-bind('css.margin');
}

.goal-line-chart__svg {
  width: 100%;
  height: 100%;
  display: block;
  overflow: visible;
}

.pill {
  position: absolute;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: v-bind('css.pillMinWidth');
  max-width: v-bind('css.pillMaxWidth');
  min-height: v-bind('css.pillMinHeight');
  max-height: v-bind('css.pillMaxHeight');
  padding: 4px 14px;
  border-radius: 9999px;
  background-color: v-bind('css.pillBg');
  border: 1px solid v-bind('css.pillBorder');
  color: v-bind('css.pillText');
  font-size: v-bind('css.pillFontSize');
  font-weight: v-bind('css.pillFontWeight');
  line-height: 1.2;
  box-sizing: border-box;
  text-align: center;
  white-space: nowrap;

  :deep(.ProseMirror) {
    min-height: 0;
    padding: 0;
    text-align: center;
  }
  :deep(p) {
    margin: 0;
    white-space: nowrap;
  }
}

.pill-text {
  display: block;
  min-width: 0;
}

.pill-start {
  bottom: v-bind('startPillBottom');
  left: 0;
}

.pill-end {
  bottom: v-bind('endPillBottom');
  right: 0;
}

:root[data-layout='responsive'] .goal-line-chart {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  aspect-ratio: auto;
  max-height: none;

  &::before {
    content: '';
    grid-area: 1 / 1;
    padding-top: v-bind('startPillRoom');
  }
}

:root[data-layout='responsive'] .goal-line-chart__svg {
  grid-area: 2 / 1;
  height: auto;
  max-height: v-bind('css.chartHeight');
  margin-top: calc(-1 * v-bind('startPillRoom'));
}

:root[data-layout='responsive'] .pill-start {
  position: static;
  grid-area: 1 / 1;
  align-self: end;
  justify-self: start;
}

:root[data-layout='responsive'] .pill-end {
  position: static;
  grid-area: 2 / 1;
  align-self: end;
  justify-self: end;
  margin-bottom: v-bind('endPillLift');
}

.caption {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 2px;
  color: v-bind('css.captionColor');
  font-size: v-bind('css.captionFontSize');
  font-weight: v-bind('css.captionFontWeight');
  line-height: 1.15;
  padding: 0 4px;
  box-sizing: border-box;
  white-space: nowrap;

  &.caption-start {
    text-align: left;
  }
  &.caption-end {
    text-align: right;
  }

  :deep(.ProseMirror) {
    min-height: 0;
    padding: 0;
  }
  :deep(p) {
    margin: 0;
    white-space: nowrap;
  }
}

.caption-line {
  display: block;
  min-width: 0;
}

.caption-sub {
  color: v-bind('css.captionSubColor');
  font-size: v-bind('css.captionSubFontSize');
  font-weight: normal;
}

.size-probe {
  position: absolute;
  left: -9999px;
  top: 0;
  width: 0;
  visibility: hidden;
  pointer-events: none;
  height: v-bind('captionDistanceCss');
}
</style>
